import React from 'react';
import { BookOpen, Award, Clock, TrendingUp, Target, CheckCircle } from 'lucide-react';

const ProgressOverview = ({ userStats, modules }) => {
  const totalModules = modules?.length || 0;
  const completionRate = totalModules > 0 ? Math.round((userStats?.modulesCompleted || 0) / totalModules * 100) : 0;
  const averageScore = userStats?.averageQuizScore || 0;
  
  const getNextModule = () => {
    if (!modules) return null;
    return modules?.find(module => 
      !module.progress || 
      (module.progress?.status !== 'completed' && module.progress?.status !== 'in_progress')
    );
  };

  const getCurrentModule = () => {
    if (!modules) return null;
    return modules?.find(module => module.progress?.status === 'in_progress');
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const nextModule = getNextModule();
  const currentModule = getCurrentModule();
  const recommendedModule = currentModule || nextModule;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Your Learning Progress</h2>
        <p className="text-gray-600 text-sm mt-1">Track your IPM mastery and achievements</p>
      </div>
      <div className="p-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <BookOpen className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-900">{userStats?.modulesCompleted || 0}</div>
            <div className="text-sm text-green-700">Completed</div>
          </div>

          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-900">{userStats?.modulesInProgress || 0}</div>
            <div className="text-sm text-blue-700">In Progress</div>
          </div>

          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <Award className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-yellow-900">{userStats?.certificatesEarned || 0}</div>
            <div className="text-sm text-yellow-700">Certificates</div>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-purple-900">{formatTime(userStats?.totalTimeSpent || 0)}</div>
            <div className="text-sm text-purple-700">Time Invested</div>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-medium text-gray-900">Overall Completion</h3>
            <span className="text-2xl font-bold text-green-600">{completionRate}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div 
              className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${completionRate}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between text-sm text-gray-600">
            <span>{userStats?.modulesCompleted || 0} of {totalModules} modules completed</span>
            <span>{totalModules - (userStats?.modulesCompleted || 0)} remaining</span>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Quiz Performance */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Quiz Performance</h4>
              <Target className="h-5 w-5 text-gray-400" />
            </div>
            
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {averageScore?.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 mb-3">Average Score</div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all ${
                  averageScore >= 80 ? 'bg-green-500' : 
                  averageScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(averageScore, 100)}%` }}
              ></div>
            </div>
            
            <div className="text-xs text-gray-500 mt-2">
              {averageScore >= 80 ? 'Excellent performance!' : 
               averageScore >= 60 ? 'Good progress, keep improving' : 'Focus on reviewing concepts'}
            </div>
          </div>

          {/* Learning Streak */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Recent Activity</h4>
              <TrendingUp className="h-5 w-5 text-gray-400" />
            </div>
            
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {Math.ceil((userStats?.totalTimeSpent || 0) / 60)}
            </div>
            <div className="text-sm text-gray-600 mb-3">Hours This Month</div>
            
            <div className="flex items-center text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span className="text-gray-600">
                {userStats?.modulesCompleted || 0} modules mastered
              </span>
            </div>
          </div>
        </div>

        {/* Recommended Next Action */}
        {recommendedModule && (
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-medium mb-2">
                  {currentModule ? 'Continue Your Learning' : 'Ready for Next Module'}
                </h3>
                <p className="text-green-100 mb-3">
                  Chapter {recommendedModule?.chapterNumber}: {recommendedModule?.title}
                </p>
                <p className="text-green-50 text-sm mb-4">
                  {recommendedModule?.description}
                </p>
                
                <div className="flex items-center space-x-4 text-sm text-green-100">
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {recommendedModule?.estimatedDuration} min
                  </span>
                  <span className="flex items-center">
                    <Target className="h-4 w-4 mr-1" />
                    {recommendedModule?.difficulty}
                  </span>
                  {recommendedModule?.isPremium && (
                    <span className="bg-white/20 px-2 py-1 rounded-md text-xs">Premium</span>
                  )}
                </div>
              </div>
              
              <button className="bg-white text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors font-medium ml-4">
                {currentModule ? 'Continue' : 'Start Module'}
              </button>
            </div>
          </div>
        )}

        {/* Completion Message */}
        {completionRate === 100 && (
          <div className="bg-gradient-to-r from-green-400 to-green-600 text-white rounded-lg p-6 text-center">
            <Award className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Congratulations!</h3>
            <p className="text-green-100 mb-4">
              You've completed the entire IPM & TPM curriculum. You're now certified in integrated pest management!
            </p>
            <button className="bg-white text-green-600 px-6 py-2 rounded-lg hover:bg-green-50 transition-colors font-medium">
              Download Certificate
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressOverview;